import React, { useCallback, useMemo } from "react";
import {
	Autocomplete,
	AutocompleteProps,
	FormControl,
	FormHelperText,
	InputLabel,
	Select,
} from "@mui/material";
import { InputError } from "./types";
import { decodeNativeSelectKey, encodeNativeSelectKey } from "./utils";
import { AavoTextField } from "./AavoTextField";
import { isTouchDevice } from "src/utils/isTouchDevice";

export interface MultiSelectFieldProps<T, Key>
	extends Omit<
		AutocompleteProps<T, true, false, false>,
		| "onChange"
		| "renderInput"
		| "getOptionKey"
		| "getOptionLabel"
		| "freeSolo"
		| "multiple"
		| "disableClearable"
		| "isOptionEqualToValue"
		| "value"
		| "ref"
	> {
	getOptionKey: (o: T) => Key;
	getOptionLabel: (o: T) => string;
	label: string;
	error?: InputError;
	onChange: (v: Key[]) => void;
	value: Key[];
}

export const MultiSelectField = <T, Key>(props: MultiSelectFieldProps<T, Key>) => {
	if (isTouchDevice()) return <NativeMultiSelectField {...props} />;
	else return <AutoCompleteMultiSelectField {...props} />;
};

const NativeMultiSelectField = <T, Key>({
	label,
	value,
	onChange,
	options,
	getOptionKey,
	getOptionLabel,
	error,
	autoFocus,
	sx,
}: MultiSelectFieldProps<T, Key>) => {
	const encodedValue = useMemo(() => value.map((v) => encodeNativeSelectKey(v)), [value]);

	const onChangeDecoded = useCallback(
		(e: any) => {
			onChange(decodeMultiSelectFieldValue(e));
		},
		[onChange],
	);

	return (
		<FormControl fullWidth error={error !== undefined} sx={sx}>
			<InputLabel htmlFor={"multiple-selection-label"} shrink>
				{label}
			</InputLabel>
			<Select
				autoFocus={autoFocus}
				multiple={true}
				native={true}
				inputProps={{
					id: "multiple-selection-label",
				}}
				onChange={onChangeDecoded}
				value={encodedValue}
			>
				{options.map((o, index) => {
					return (
						<option
							key={`select-field-option-${index}`}
							value={encodeNativeSelectKey(getOptionKey(o))}
						>
							{getOptionLabel(o)}
						</option>
					);
				})}
			</Select>
			<FormHelperText>{error ?? ""}</FormHelperText>
		</FormControl>
	);
};

const AutoCompleteMultiSelectField = <T, Key>({
	label,
	value,
	onChange,
	options,
	getOptionKey,
	getOptionLabel,
	error,
	autoFocus,
	...other
}: MultiSelectFieldProps<T, Key>) => {
	const optionsForValue = useMemo(
		() =>
			options.filter((o: T) => {
				return value.includes(getOptionKey(o));
			}),
		[options, value, getOptionKey],
	);

	return (
		<Autocomplete
			renderInput={(params) => {
				return (
					<AavoTextField
						{...params}
						fullWidth
						label={label}
						error={error !== undefined}
						helperText={error ?? undefined}
						autoFocus={autoFocus}
					/>
				);
			}}
			options={options}
			getOptionLabel={getOptionLabel}
			isOptionEqualToValue={(o1, o2) => {
				return getOptionKey(o1) === getOptionKey(o2);
			}}
			multiple={true}
			value={optionsForValue}
			onChange={(_, value) => {
				onChange(value.map(getOptionKey));
			}}
			{...other}
		/>
	);
};

const decodeMultiSelectFieldValue = <Key,>(event: React.ChangeEvent<HTMLSelectElement>): Key[] => {
	const { options } = event.target;
	const selectedItems: Key[] = [];
	for (let i = 0; i < options.length; i += 1) {
		const option = options[i];
		if (option && option.selected) {
			const selectedItem = decodeNativeSelectKey(option.value);
			if (selectedItem !== undefined) {
				selectedItems.push(selectedItem);
			}
		}
	}
	return selectedItems;
};
